import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin, Linkedin, Facebook, Instagram } from "lucide-react";
import { getApiBaseUrl } from "../../config";

/* ===== Site footer =====
   Newsletter signup + 3-column sitemap + legal line.
*/

const COLS = [
  {
    title: "Adel Group",
    links: [
      { t: "Home", to: "/" },
      { t: "For employers", to: "/employers" },
      { t: "For jobseekers", to: "/candidates" },
      { t: "FAQ", to: "/faq" },
      { t: "Trust & licensing", to: "/trust" },
      { t: "Contact", to: "/employers#brief" },
    ],
  },
  {
    title: "For employers",
    links: [
      { t: "Hire workforce", to: "/employers" },
      { t: "How we work", to: "/employers#process" },
      { t: "Sectors we staff", to: "/employers#sectors" },
      { t: "Send a brief", to: "/employers#brief" },
    ],
  },
  {
    title: "For jobseekers",
    links: [
      { t: "Find a job", to: "/candidates" },
      { t: "Your journey", to: "/candidates#journey" },
      { t: "What to expect", to: "/candidates#expect" },
      { t: "Submit your CV", to: "/candidates/apply" },
    ],
  },
];

export default function D13Footer() {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState(""); // honeypot — bots fill this, humans don't
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || status === "sending") return;
    setStatus("sending");
    setErrMsg("");
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), honeypot: hp }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Something went wrong. Please try again.");
      }
      setStatus("ok");
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <footer className="d13-footer">
      {/* Newsletter block */}
      <div className="d13-footer__nl">
        <div className="d13-container">
          <div className="d13-footer__nl-inner">
            <div className="d13-footer__nl-text">
              <span className="d13-eyebrow d13-eyebrow--on-dark">Stay in touch</span>
              <h3 className="d13-footer__nl-t">Leave us your email.</h3>
              <p className="d13-footer__nl-p">
                Whether you're hiring or looking for work, share your email and our team will get back to you.
              </p>
            </div>
            <form className="d13-footer__nl-form" onSubmit={submit}>
              {status === "ok" ? (
                <motion.span className="d13-footer__nl-ok"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                  Thanks — we'll be in touch.
                </motion.span>
              ) : (
                <>
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Email address"
                    required
                    disabled={status === "sending"}
                  />
                  {/* Honeypot: hidden from people, catches bots. Not a real field. */}
                  <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  />
                  <button type="submit" disabled={status === "sending"}>
                    {status === "sending" ? "Sending…" : (<>Send <ArrowRight size={16} strokeWidth={2.2} /></>)}
                  </button>
                </>
              )}
            </form>
            {status === "error" && (
              <p role="alert" style={{ margin: "10px 0 0", color: "#ffd7d0", fontSize: "0.85rem" }}>
                {errMsg}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sitemap */}
      <div className="d13-footer__body">
        <div className="d13-container">
          <div className="d13-footer__grid">
            <div className="d13-footer__brand">
              <img src="/adel-logo-dark.png" alt="Adel Group" />
              <p className="d13-footer__tag">
                Connecting Talents. Creating Opportunities. Kenya → international, since 2019.
              </p>
              <ul className="d13-footer__contact">
                <li><Phone size={14} /> <a href="tel:+254117222777">+254 117 222 777</a></li>
                <li><Mail size={14} /> <a href="mailto:info@adelgroups.com">info@adelgroups.com</a></li>
                <li><MapPin size={14} /> Room 12, 6th floor, View Park Towers, Utalii Lane, Nairobi</li>
              </ul>
              <ul className="d13-footer__social" aria-label="Adel Group on social">
                <li><a href="https://www.linkedin.com/company/adelgroup/" aria-label="LinkedIn" target="_blank" rel="noreferrer"><Linkedin size={16} /></a></li>
                <li><a href="https://www.facebook.com/adelrecruitment.ke" aria-label="Facebook" target="_blank" rel="noreferrer"><Facebook size={16} /></a></li>
                <li><a href="https://www.instagram.com/adelrecruitment.ke" aria-label="Instagram" target="_blank" rel="noreferrer"><Instagram size={16} /></a></li>
              </ul>
            </div>

            {COLS.map((c) => (
              <div key={c.title} className="d13-footer__col">
                <h4>{c.title}</h4>
                <ul>
                  {c.links.map((l) => (
                    <li key={l.t}><Link to={l.to}>{l.t}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legal strip */}
      <div className="d13-footer__legal">
        <div className="d13-container">
          <div className="d13-footer__legal-inner">
            <span>© 2026 Adel Group. All rights reserved.</span>
            <span>NEA licensed · RA/2025/09/56 · <Link to="/trust">Verify us</Link></span>
            <span>Recruitment partner since 2019 · Nairobi, Kenya</span>
            <span className="d13-footer__legal-hours">Office hours: Mon–Sat, 08:00–17:00 EAT</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
